<template>
  <div>
    <loading v-if="isLoading" class="flex justify-center mt-5 py-10" />
    <div v-else class="page">
      <h1 class="mb-4 font-semibold text-2xl">Study Dashboard</h1>
      <p class="mb-2">
        General studies let participants log their own device-collected data to monitor progress.
      </p>
      <p class="mb-4">
        Clinician studies enable healthcare professionals to capture patient exercises directly from
        medical devices.
      </p>
      <p class="mb-4 font-medium">
        Note: Disabled cards indicate studies without any current participants or data.
      </p>

      <StudiesClinicianList :studies="studies.clinician" @select="handleSelectStudyClinician" />
      <StudiesGeneralList :studies="studies.general" @select="handleSelectStudyGeneral" />
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { toast } from 'vue3-toastify';
import { useRouter } from 'vue-router';
import { ROUTES } from '@/router';
import { getStudies, getUsers, getStudiesClinician } from '@/studies/services/studies';
import StudiesClinicianList from '@/studies/atoms/StudiesClinicianList.vue';
import StudiesGeneralList from '@/studies/atoms/StudiesGeneralList.vue';
import Loading from '@/components/loading.vue';

const router = useRouter();
const isLoading = ref(false);
const studies = reactive({ clinician: [], general: [] });

const handleSelectStudyGeneral = async (study = { id: '', hasUsers: false }) => {
  if (!study.hasUsers) {
    toast.info('This study has no users—please select another.');
    return;
  }
  await router.push(`/${ROUTES.STUDY.LIST}/${study.id}`);
};

const handleSelectStudyClinician = async (study) => {
  await router.push({
    name: ROUTES.STUDY.CLINICIAN.PATIENTS,
    params: { clinicianStudyId: study.id },
  });
};

async function loadStudies() {
  isLoading.value = true;
  try {
    const [generalData, clinData] = await Promise.all([getStudies(), getStudiesClinician()]);
    const generalWithUsers = await Promise.all(
      generalData.map(async (s) => {
        const users = await getUsers({ studyId: s.id });
        return { ...s, hasUsers: users.length > 0 };
      })
    );
    studies.clinician = clinData;
    studies.general = generalWithUsers.sort((a, b) => b.hasUsers - a.hasUsers);
  } catch (err) {
    toast.error(err.message);
  } finally {
    isLoading.value = false;
  }
}

onMounted(loadStudies);
</script>

<style scoped lang="scss">
.grid-studies {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1.5rem;
  margin: 1rem 0;
}

.mt-5 {
  margin-top: 1.25rem;
}
</style>
